import { Program } from "../../../../vendor/ogl/src/core/Program";
import { Vec2 } from "../../../../vendor/ogl/src/math/Vec2";
import { Texture } from "../../../../vendor/ogl/src/core/Texture";
import { Vec3 } from "../../../../vendor/ogl/src/math/Vec3";
const { GPGPU } = require("../../../../vendor/ogl/src/extras/GPGPU");

const prevPosKernel = require('./kernels/prevPos.frag');
const currentPosKernel = require('./kernels/currentPos.frag');
const positionKernel = require('./kernels/position.frag');
const normalKernel = require('./kernels/calcNormal.frag');

const restlengthKernel = require('./kernels/restLength.frag');
const restLengthDiagonalKernel = require('./kernels/restLengthDiagonal.frag');

const constrainKernel = require('./kernels/constrain.frag');
const constrainDiagonalKernel = require('./kernels/constrainDiagonal.frag');

const constrainHorizontalKernel = require('./kernels/constrainHorizontal.frag');
const constrainVerticalKernel = require('./kernels/constrainVertical.frag');
const constrainTLTRKernel = require('./kernels/constrainTLTR.frag');
const constrainBLBRKernel = require('./kernels/constrainBLBR.frag');

import {params} from '../../../params.js';

export class Simulator {

    constructor(gl, {
        data,
        countX,
        countY,
    }) {

        this.gl = gl;

        this.data = data;

        this.countX = countX;
        this.countY = countY;

        this.firstTick = true;

        this.initSimulators();
        this.initPrograms();

    }

    initSimulators() {

        this.positionData = new Float32Array(this.countX*this.countY*4);
        let positionDataIterator = 0;
        let origDataIterator = 0;
        
        for(let y = 0; y < this.countY; y++) {
            
            for(let x = 0; x < this.countX; x++) {

                this.positionData[positionDataIterator++] = this.data[origDataIterator++];
                this.positionData[positionDataIterator++] = this.data[origDataIterator++];
                this.positionData[positionDataIterator++] = this.data[origDataIterator++];
                
                let corner = 0;

                let isTopLeft = y === 0 && x === 0;
                let isTopRight = y === 0 && x === this.countX - 1;
                let isBottomLeft = y === this.countY-1 && x === this.countX -1;
                let isBottomRight = y === this.countY-1 && x === 0;

                if(isTopLeft) corner = 1;
                if(isTopRight) corner = 2;
                if(isBottomLeft) corner = 3;
                if(isBottomRight) corner = 4;
               
                this.positionData[positionDataIterator++] = corner;

            }

        }

        this.currentPosCapture = new GPGPU(this.gl, {
            data: this.positionData,
            type: this.gl.FLOAT
        });

        const prevPositionData = this.positionData.slice();
        let prevPosIterator = 0;
        origDataIterator = 0;
        let twoPI = Math.PI * 2;
            
            for(let x = 0; x < this.countX*this.countY; x++) {

                let offsetx = Math.random() * 2.0 - 1.0;
                let offsety = Math.random() * 2.0 - 1.0;
                let offsetz = Math.random() * 2.0 - 1.0;

                prevPositionData[prevPosIterator++] += (offsetx*0.);
                prevPositionData[prevPosIterator++] += (offsety*0.);
                prevPositionData[prevPosIterator++] += (offsetz*0.);
                prevPositionData[prevPosIterator++] += 0.0;

        }

        this.prevPositionCapture = new GPGPU(this.gl, {
            data: prevPositionData,
            type: this.gl.FLOAT
        });

        this.positionSim = new GPGPU(this.gl, {
            data: this.positionData,
            type: this.gl.FLOAT,
        });

        const normalData = new Float32Array(this.countX*this.countY*4.0);
        let normalIterator = 0;
        
        for(let i = 0; i < this.countX*this.countY; i++) {

            normalData[normalIterator++] = 0.0;
            normalData[normalIterator++] = 0.0;
            normalData[normalIterator++] = -1.0;
            normalData[normalIterator++] = 0.0;

        }

        this.normalSim = new GPGPU(this.gl, {
            data: normalData,
            type: this.gl.FLOAT,
            // filtering: this.gl.LINEAR
        });

        this.initPositions = this.createDataTexture({data: this.positionData, size: this.countX});

        this.restlengthCapture = new GPGPU(this.gl, {
            data: this.positionData,
            type: this.gl.FLOAT
        });

        this.restlengthDiagonalCapture = new GPGPU(this.gl, {
            data: this.positionData,
            type: this.gl.FLOAT
        });
        
        
        const restlengthCaptureU = {
            _InitPos: {
                value: this.initPositions
            },
            _Size: {
                value: params.CLOTH.SIZE
              },
        }

        this.restlengthCapture.addPass({
            fragment: restlengthKernel,
            uniforms: restlengthCaptureU
        });

        const restlengthDiagonalU = {
            _InitPos: {
                value: this.initPositions
            },
            _Size: {
                value: params.CLOTH.SIZE
              },
        }

        this.restlengthDiagonalCapture.addPass({
            fragment: restLengthDiagonalKernel,
            uniforms: restlengthDiagonalU
        });

        this.prewarm();

    }

    //TODO:
    //MAKE SURE THE LAST PIXEL IN EACH ROW IS NOT PROCESSED
    //DOUBLE CHECK THE MIX CONDITION I ADDED

    initPrograms() {

        const normalSimU = {
            _Position: this.positionSim.uniform,
            _Size: {
                value: params.CLOTH.SIZE
              },
        }

        this.normalSim.addPass({
            fragment: normalKernel,
            uniforms: normalSimU
        });

        const positionSimU = {
            _PrevPos: this.prevPositionCapture.uniform,
            _CurrentPos: this.currentPosCapture.uniform,
            _Normal: this.normalSim.uniform,
            _Force: {
                value: new Vec3(0.0, 0.0, 0.0)
            },
            _Time: {
                value: 0.0
            },
            _InputWorldPos: {
                value: new Vec3(0.0,0.0,0.0)
            },
            _IsInteracting: {
                value: false
            },
            _Corner: {
                value: 0
            },
            _Size: {
                value: params.CLOTH.SIZE
              },
              _TimeStep: {
                  value: 0.0
              }
        }

        this.positionSim.addPass({
            fragment: positionKernel,
            uniforms: positionSimU
        });

        const constrainU = {

            _RestLengthVH: this.restlengthCapture.uniform,
            _RestLengthDiagonal: this.restlengthDiagonalCapture.uniform,
            _Size: {
                value: params.CLOTH.SIZE
            },
            _TexelSize: {
                value: new Vec2(1.0/params.CLOTH.SIZE)
            },
            _Clamp: {
                value: params.PHYSICS.CLAMP
            },
            _Stiffness: {
                value: params.PHYSICS.STIFFNESS
            }

        }

        for(let i = 0; i < params.PHYSICS.STEPS; i++) {
            // this.positionSim.addPass({
            //     fragment: constrainKernel,
            //     uniforms: constrainU
            // });
            // this.positionSim.addPass({
            //     fragment: constrainDiagonalKernel,
            //     uniforms: constrainU
            // });
        }

        const prevPosCaptureSimU = {
            _Positions: this.currentPosCapture.uniform,
            _Size: {
                value: params.CLOTH.SIZE
              },
        }

        this.prevPositionCapture.addPass({
            fragment: prevPosKernel,
            uniforms: prevPosCaptureSimU
        });

        const currentPosCaptureSimU = {
            _Positions: this.positionSim.uniform,
            _Size: {
                value: params.CLOTH.SIZE
              },
        }

        this.currentPosCapture.addPass({
            fragment: currentPosKernel,
            uniforms: currentPosCaptureSimU
        });

        const constrainHorizontalFirstPassU = this.createConstraintUniform({flip: 0.0, restlength: this.restlengthCapture.uniform}); 
        const constrainHorizontalSecondPassU = this.createConstraintUniform({flip: 1.0, restlength: this.restlengthCapture.uniform});
        
        const constrainVerticalFirstPassU = this.createConstraintUniform({flip: 0.0, restlength: this.restlengthCapture.uniform});
        const constrainVerticalSecondPassU = this.createConstraintUniform({flip: 1.0, restlength: this.restlengthCapture.uniform});

        const constrainTLTRfirstPasssU = this.createConstraintUniform({flip: 0.0, restlength: this.restlengthDiagonalCapture.uniform}); 
        const constrainTLTRsecondPasssU = this.createConstraintUniform({flip: 1.0, restlength: this.restlengthDiagonalCapture.uniform});
        
        const constrainBRBLfirstPasssU = this.createConstraintUniform({flip: 0.0, restlength: this.restlengthDiagonalCapture.uniform});
        const constrainBRBLsecondPasssU = this.createConstraintUniform({flip: 1.0, restlength: this.restlengthDiagonalCapture.uniform});

        

        for(let i = 0; i < params.PHYSICS.STEPS; i++) {

            // HORIZONTAL CONSTRAINTS
            this.positionSim.addPass({
                fragment: constrainHorizontalKernel,
                uniforms: constrainHorizontalFirstPassU
            });
    
            this.positionSim.addPass({
                fragment: constrainHorizontalKernel,
                uniforms: constrainHorizontalSecondPassU
            });

            //VERTICAL CONSTRAINTS
            this.positionSim.addPass({
                fragment: constrainVerticalKernel,
                uniforms: constrainVerticalFirstPassU
            });
    
            this.positionSim.addPass({
                fragment: constrainVerticalKernel,
                uniforms: constrainVerticalSecondPassU
            });

            // DIAGONAL CONSTRAINTS

            this.positionSim.addPass({
                fragment: constrainBLBRKernel,
                uniforms: constrainBRBLfirstPasssU
            });

            this.positionSim.addPass({
                fragment: constrainBLBRKernel,
                uniforms: constrainBRBLsecondPasssU
            });

            this.positionSim.addPass({
                fragment: constrainTLTRKernel,
                uniforms: constrainTLTRfirstPasssU
            });

            
            this.positionSim.addPass({
                fragment: constrainTLTRKernel,
                uniforms: constrainTLTRsecondPasssU
            });

            }

    }

    createDataTexture({
        data,
        size
    }) {

        return new Texture(this.gl, {
            image: data,
            target: this.gl.TEXTURE_2D,
            type: this.gl.FLOAT,
            format: this.gl.RGBA,
            internalFormat: this.gl.renderer.isWebgl2 ? this.gl.RGBA32F : this.gl.RGBA,
            wrapS: this.gl.CLAMP_TO_EDGE,
            wrapT: this.gl.CLAMP_TO_EDGE,
            generateMipmaps: false,
            minFilter: this.gl.NEAREST,
            magFilter: this.gl.NEAREST,
            width: size,
            height: size,
            flipY: false
        })

    }

    createConstraintUniform({
        flip,
        restlength
    }) {
       

    const uniform = {

        _TexelSize: {
            value: new Vec2(1.0 / params.CLOTH.SIZE, 1.0 / params.CLOTH.SIZE)
        },
        _Stiffness: {
            value: params.PHYSICS.STIFFNESS
        },
        _Flip: {
            value: flip
        },
        _RestLength: restlength,
        _Clamp: {
            value: params.PHYSICS.CLAMP
        },
        _Size: {
            value: params.CLOTH.SIZE
          },

    }

       return uniform 
    }

    prewarm() {

        this.cornerUpdated = false;
        this.restlengthCapture.render();
        this.restlengthDiagonalCapture.render();

    }

    update(t, {
        isInteracting,
        inputWorldPos,
        dt
    }) {

        this.currentPosCapture.render();   

        if(isInteracting) {
            if(this.cornerUpdated === false) {
                const corner = (Math.floor(Math.random() * 4) + 1);
                this.positionSim.passes[0].program.uniforms._Corner.value = corner;
                this.cornerUpdated = true;
            }
        } else {
            this.cornerUpdated = false;
        }

        this.positionSim.passes[0].program.uniforms._Time.value = t;
        this.positionSim.passes[0].program.uniforms._IsInteracting.value = isInteracting;
        this.positionSim.passes[0].program.uniforms._InputWorldPos.value.copy(inputWorldPos);
        this.positionSim.passes[0].program.uniforms._TimeStep.value = dt;


        this.positionSim.render();
        this.prevPositionCapture.render();

        this.normalSim.render();
        
    }

    get Positions() {
        return this.positionSim.fbo.read.texture
    }

    get Normals() {
        return this.normalSim.fbo.read.texture
    }

    get RestLengthsOrtho() {
        return this.restlengthCapture.fbo.read.texture
    }

    get RestLengthsDiagonal() {
        return this.restlengthDiagonalCapture.fbo.read.texture
    }

}